import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MATERIAL_TRANSFERS_API_URL, DEFAULT_LIST_PAGE_SIZE } from '../constants';
import {
  MaterialTransfersGrid,
  type MaterialTransferGridRow,
  type MaterialTransferListStatusFilter
} from '../components/grids/MaterialTransfersGrid';

type PageDto = { items: MaterialTransferGridRow[]; totalCount: number; page: number; pageSize: number };

export function MaterialTransfersPage(props: {
  authFetch: (url: string) => Promise<Response>;
  selectedMaterialTransferId: string | null;
  onSelectMaterialTransfer: (id: string | null) => void;
  onOpenTransferForEdit: (id: string) => void;
}): ReactElement {
  const { authFetch, selectedMaterialTransferId, onSelectMaterialTransfer, onOpenTransferForEdit } = props;
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [data, setData] = useState<PageDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<MaterialTransferListStatusFilter>('all');

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const u = new URL(MATERIAL_TRANSFERS_API_URL);
    u.searchParams.set('page', String(page));
    u.searchParams.set('pageSize', String(pageSize));
    if (statusFilter !== 'all') {
      u.searchParams.set('status', statusFilter);
    }
    try {
      const res = await authFetch(u.toString());
      if (!res.ok) {
        setData(null);
        setErr('Could not load material transfers.');
        return;
      }
      const json = (await res.json()) as PageDto;
      setData(json);
    } catch {
      setData(null);
      setErr('Could not load material transfers.');
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, pageSize, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <MaterialTransfersGrid
      error={err}
      rows={data?.items ?? []}
      loading={loading}
      selectedId={selectedMaterialTransferId}
      onSelectRow={onSelectMaterialTransfer}
      page={data?.page ?? page}
      pageSize={data?.pageSize ?? pageSize}
      totalCount={data?.totalCount ?? 0}
      onPageChange={setPage}
      onPageSizeChange={(n) => {
        setPage(1);
        setPageSize(n);
      }}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      onNewTransfer={() => void navigate('/material-transfers/new')}
      onOpenTransferEdit={onOpenTransferForEdit}
    />
  );
}
